import React, { useState } from 'react';
import './HelpPage.css';

interface Section {
  id: string;
  title: string;
  icon: string;
}

const SECTIONS: Section[] = [
  { id: 'overview', title: 'Overview', icon: '🏠' },
  { id: 'quickstart', title: 'Quick Start', icon: '🚀' },
  { id: 'pipeline', title: 'Article Generation Pipeline', icon: '⚙️' },
  { id: 'dashboard', title: 'Dashboard', icon: '📋' },
  { id: 'articles', title: 'Working with Articles', icon: '📝' },
  { id: 'scores', title: 'Quality Scores', icon: '📊' },
  { id: 'sources', title: 'Managing Sources', icon: '📡' },
  { id: 'revision', title: 'Revisions & Feedback', icon: '✎' },
  { id: 'publish', title: 'Publishing & Export', icon: '📢' },
  { id: 'tips', title: 'Tips & Best Practices', icon: '💡' },
  { id: 'faq', title: 'FAQ', icon: '❓' },
];

const HelpPage: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [activeSection, setActiveSection] = useState('overview');

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const el = document.getElementById(`help-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="help-overlay">
      <div className="help-container">
        {/* Header */}
        <div className="help-header">
          <div className="help-header-left">
            <span className="help-logo">📰</span>
            <div>
              <h1 className="help-title">Help & User Guide</h1>
              <p className="help-subtitle">AI Article Studio · AlCircle Editorial Intelligence</p>
            </div>
          </div>
          <button className="help-close" onClick={onClose}>✕</button>
        </div>

        <div className="help-body">
          {/* Sidebar */}
          <nav className="help-nav">
            <p className="help-nav-label">CONTENTS</p>
            {SECTIONS.map(s => (
              <button
                key={s.id}
                className={`help-nav-item ${activeSection === s.id ? 'active' : ''}`}
                onClick={() => scrollTo(s.id)}
              >
                <span className="help-nav-icon">{s.icon}</span>
                {s.title}
              </button>
            ))}
          </nav>

          {/* Content */}
          <div className="help-content">

            {/* ── OVERVIEW ─────────────────────────────────── */}
            <section id="help-overview" className="help-section">
              <h2><span>🏠</span> Overview</h2>
              <p className="help-lead">
                <strong>AI Article Studio</strong> is an AI-powered newsroom platform built for the
                aluminium industry. It automatically discovers relevant news, extracts verified facts,
                generates publication-ready articles, and routes them through editorial quality checks
                — all within a single workflow.
              </p>

              <div className="help-cards">
                <div className="help-card">
                  <div className="help-card-icon">🔍</div>
                  <h4>Discover</h4>
                  <p>Monitors global aluminium industry sources and surfaces the most relevant stories.</p>
                </div>
                <div className="help-card">
                  <div className="help-card-icon">🧠</div>
                  <h4>Extract</h4>
                  <p>Builds a verified Fact Bank from source evidence — no hallucinated data.</p>
                </div>
                <div className="help-card">
                  <div className="help-card-icon">✍️</div>
                  <h4>Generate</h4>
                  <p>Creates structured, factually accurate articles grounded in source material.</p>
                </div>
                <div className="help-card">
                  <div className="help-card-icon">✅</div>
                  <h4>Validate</h4>
                  <p>Runs fidelity, originality, style, and claim verification before release.</p>
                </div>
              </div>

              <div className="help-notice">
                <strong>📌 Important:</strong> Publication is disabled by default. Every article
                requires human editor approval before it can be published. The system is designed as
                an editorial assistant, not an autonomous publisher.
              </div>
            </section>

            {/* ── QUICK START ──────────────────────────────── */}
            <section id="help-quickstart" className="help-section">
              <h2><span>🚀</span> Quick Start</h2>

              <div className="help-steps">
                <div className="help-step">
                  <div className="help-step-num">1</div>
                  <div className="help-step-body">
                    <h4>Sign in</h4>
                    <p>Use your account credentials on the login page. Contact your administrator if you need access.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="help-step-num">2</div>
                  <div className="help-step-body">
                    <h4>Click Generate Articles</h4>
                    <p>Press the <strong>▶ Generate Articles</strong> button in the top-right header. The pipeline starts immediately on the server.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="help-step-num">3</div>
                  <div className="help-step-body">
                    <h4>Switch tabs freely</h4>
                    <p>The pipeline runs in the background on the server. You can close or switch browser tabs — generation will finish on its own. Check back anytime.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="help-step-num">4</div>
                  <div className="help-step-body">
                    <h4>Review generated articles</h4>
                    <p>When complete, articles appear in the dashboard grid. Each shows quality scores and status.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="help-step-num">5</div>
                  <div className="help-step-body">
                    <h4>Edit, revise, and publish</h4>
                    <p>Open any article to edit, request AI revisions, check scores, and publish when satisfied.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* ── PIPELINE ─────────────────────────────────── */}
            <section id="help-pipeline" className="help-section">
              <h2><span>⚙️</span> Article Generation Pipeline</h2>
              <p>
                When you click <strong>Generate Articles</strong>, a 5-stage AI pipeline runs on the
                server. It is fully autonomous and continues even if you navigate away.
              </p>

              <div className="help-pipeline-stages">
                <div className="help-pipeline-stage">
                  <div className="help-stage-num">1</div>
                  <div className="help-stage-icon">🔍</div>
                  <div className="help-stage-body">
                    <h4>News Discovery</h4>
                    <p>
                      Scans configured RSS feeds, market data sources, company news, and government
                      releases. Filters by relevance to the aluminium industry, checks recency, and
                      removes duplicates and syndicated copies. Assigns each source a trust tier
                      (A / B / C) and eligibility status.
                    </p>
                    <div className="help-tag-row">
                      <span className="help-tag green">Tier A: LME, SMM, Reuters</span>
                      <span className="help-tag blue">Tier B: Trade publications</span>
                      <span className="help-tag grey">Tier C: Flagged for review</span>
                    </div>
                  </div>
                </div>

                <div className="help-pipeline-stage">
                  <div className="help-stage-num">2</div>
                  <div className="help-stage-icon">📋</div>
                  <div className="help-stage-body">
                    <h4>Fact Bank Creation</h4>
                    <p>
                      Extracts every material fact from the source text into a structured Fact Bank —
                      including prices, volumes, dates, company actions, quotes, and policy details.
                      Every fact is stamped with its source paragraph ID and verbatim evidence span.
                      Facts must be grounded in actual source text before they can enter generation.
                    </p>
                    <div className="help-notice-small">
                      <strong>Key safety rule:</strong> The generator receives only the Fact Bank —
                      never the raw source text. This prevents copying and hallucination.
                    </div>
                  </div>
                </div>

                <div className="help-pipeline-stage">
                  <div className="help-stage-num">3</div>
                  <div className="help-stage-icon">✍️</div>
                  <div className="help-stage-body">
                    <h4>Article Generation</h4>
                    <p>
                      Generates structured articles paragraph-by-paragraph. Each paragraph is
                      produced from a controlled subset of facts, with semantic ASSERTION blocks
                      specifying subject, relation, metric, value, and comparator. Quotes are
                      inserted deterministically — the AI is not asked to reproduce quotes from
                      memory.
                    </p>
                  </div>
                </div>

                <div className="help-pipeline-stage">
                  <div className="help-stage-num">4</div>
                  <div className="help-stage-icon">🎨</div>
                  <div className="help-stage-body">
                    <h4>Style & Originality Analysis</h4>
                    <p>
                      Scores the article against AlCircle editorial guidelines across structure,
                      terminology, data presentation, tone, and coherence. Also measures originality
                      — articles too similar to the source text are flagged for rewrite. The
                      originality threshold is 70%.
                    </p>
                  </div>
                </div>

                <div className="help-pipeline-stage">
                  <div className="help-stage-num">5</div>
                  <div className="help-stage-icon">✅</div>
                  <div className="help-stage-body">
                    <h4>Quality Checks</h4>
                    <p>
                      Runs three parallel gates: <strong>Fidelity</strong> (all facts in the article
                      match the source), <strong>Claim Verification</strong> (every factual
                      statement is supported by the Fact Bank), and <strong>Grounding Check</strong>
                      (no fabricated entities, quotes, or numbers). Articles that fail a gate are
                      routed to <em>needs_review</em> for editor attention.
                    </p>
                  </div>
                </div>
              </div>

              <div className="help-notice">
                <strong>Background execution:</strong> The pipeline runs entirely on the server.
                You do <em>not</em> need to keep the browser tab open. The status updates
                automatically when you return to the dashboard.
              </div>
            </section>

            {/* ── DASHBOARD ────────────────────────────────── */}
            <section id="help-dashboard" className="help-section">
              <h2><span>📋</span> Dashboard</h2>

              <h3>Header Controls</h3>
              <div className="help-table-wrap">
                <table className="help-table">
                  <thead><tr><th>Control</th><th>What it does</th></tr></thead>
                  <tbody>
                    <tr><td><strong>▶ Generate Articles</strong></td><td>Starts the full pipeline. Returns immediately — pipeline runs in background.</td></tr>
                    <tr><td><strong>📡 Sources</strong></td><td>Opens the source management panel to add, enable, or disable news feeds.</td></tr>
                    <tr><td><strong>📊 View Logs</strong></td><td>Opens the live generation log viewer for debugging or monitoring pipeline progress.</td></tr>
                  </tbody>
                </table>
              </div>

              <h3>Filtering Articles</h3>
              <p>Use the tab bar to filter by status:</p>
              <ul className="help-list">
                <li><strong>All Articles</strong> — everything generated, including drafts and published pieces</li>
                <li><strong>Drafts</strong> — articles awaiting editor review</li>
                <li><strong>Published</strong> — articles approved and marked published</li>
              </ul>
              <p>Use the category filter dropdown to narrow by:</p>
              <div className="help-tag-row">
                {['Bauxite','Alumina','Primary Aluminium','Downstream','End-User','Recycled Aluminium','Sustainability'].map(c => (
                  <span key={c} className="help-tag blue">{c}</span>
                ))}
              </div>

              <h3>Article Card</h3>
              <p>Each card in the grid shows at a glance:</p>
              <ul className="help-list">
                <li>Generated image (AI-created or stock fallback)</li>
                <li>Headline and category badge</li>
                <li>Source name and article excerpt</li>
                <li>Quality and Style scores (color-coded)</li>
                <li>Status badge (draft / review / published)</li>
                <li>Action buttons: <strong>Edit</strong>, <strong>View</strong>, <strong>History</strong>, <strong>PDF</strong></li>
              </ul>
            </section>

            {/* ── ARTICLES ─────────────────────────────────── */}
            <section id="help-articles" className="help-section">
              <h2><span>📝</span> Working with Articles</h2>

              <h3>Opening an Article</h3>
              <p>
                Click <strong>✏️ Edit</strong> to open in edit mode (allows changes) or
                <strong> 👁️ View</strong> to open in read-only mode.
              </p>

              <h3>Edit Mode</h3>
              <p>
                In edit mode, click directly on the article body to type. The content area is a
                live text editor. When you make a change, the header shows <em>"● Unsaved changes"</em>.
                Click <strong>Save Changes</strong> to persist.
              </p>
              <div className="help-notice-small">
                Save your changes before requesting an AI revision or publishing — the
                <strong> Publish</strong> button is disabled while there are unsaved changes.
              </div>

              <h3>Article Metadata</h3>
              <p>The modal header shows:</p>
              <ul className="help-list">
                <li><strong>Category & sub-vertical</strong> — the story's industry segment</li>
                <li><strong>Tags</strong> — up to 4 content keywords</li>
                <li><strong>Sources</strong> — clickable links to every source used in the article</li>
                <li><strong>Generated on</strong> — exact creation timestamp</li>
              </ul>

              <h3>Article Statuses</h3>
              <div className="help-table-wrap">
                <table className="help-table">
                  <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
                  <tbody>
                    <tr><td><span className="status-badge draft">draft</span></td><td>Generated, awaiting editor review. Can be edited and revised.</td></tr>
                    <tr><td><span className="status-badge review">needs_review</span></td><td>Pipeline flagged an issue (style, originality, claim, or fidelity). Editor should inspect before publishing.</td></tr>
                    <tr><td><span className="status-badge published">published</span></td><td>Approved and marked published by an editor.</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* ── SCORES ───────────────────────────────────── */}
            <section id="help-scores" className="help-section">
              <h2><span>📊</span> Quality Scores</h2>
              <p>
                Every article carries four scores computed automatically by the pipeline and
                refreshable on demand.
              </p>

              <div className="help-score-grid">
                <div className="help-score-card">
                  <div className="help-score-label">Quality Score</div>
                  <div className="help-score-desc">
                    Overall article quality combining style compliance and factual confidence.
                    Derived from <code>style_match_score</code> or <code>confidence_score</code>.
                  </div>
                </div>
                <div className="help-score-card">
                  <div className="help-score-label">Style Match</div>
                  <div className="help-score-desc">
                    How closely the article follows AlCircle editorial guidelines across structure,
                    terminology, data presentation, tone, and coherence.
                  </div>
                </div>
                <div className="help-score-card">
                  <div className="help-score-label">Originality</div>
                  <div className="help-score-desc">
                    Percentage of authorial prose that differs from the source material. Must be
                    ≥ 70% to pass the originality gate automatically.
                  </div>
                </div>
                <div className="help-score-card">
                  <div className="help-score-label">Image Quality</div>
                  <div className="help-score-desc">
                    Confidence score for the AI-generated image. If generation fails, a stock
                    industry image is used as fallback.
                  </div>
                </div>
              </div>

              <div className="help-table-wrap" style={{marginTop: '1.5rem'}}>
                <table className="help-table">
                  <thead><tr><th>Score Range</th><th>Rating</th><th>Colour</th></tr></thead>
                  <tbody>
                    <tr><td>85 – 100</td><td>Excellent</td><td><span style={{color:'#22c55e'}}>■</span> Green</td></tr>
                    <tr><td>70 – 84</td><td>Good</td><td><span style={{color:'#84cc16'}}>■</span> Light green</td></tr>
                    <tr><td>50 – 69</td><td>Fair</td><td><span style={{color:'#eab308'}}>■</span> Yellow</td></tr>
                    <tr><td>0 – 49</td><td>Poor</td><td><span style={{color:'#ef4444'}}>■</span> Red</td></tr>
                  </tbody>
                </table>
              </div>

              <p style={{marginTop:'1rem'}}>
                Click <strong>Re-analyze</strong> inside any article to refresh the scores from the
                backend after making edits.
              </p>
            </section>

            {/* ── SOURCES ──────────────────────────────────── */}
            <section id="help-sources" className="help-section">
              <h2><span>📡</span> Managing Sources</h2>
              <p>
                Click <strong>📡 Sources</strong> in the header to open the source management panel.
              </p>

              <h3>Adding a Custom Source</h3>
              <ol className="help-ordered">
                <li>Click <strong>Add Source</strong> to expand the form.</li>
                <li>Enter a descriptive <strong>Source Name</strong>.</li>
                <li>Paste the <strong>Feed URL</strong> (RSS or atom feed).</li>
                <li>Choose a <strong>Category</strong> (RSS Feeds, Market Data, Company Reports, Government & Regulatory, or Custom).</li>
                <li>Click <strong>Add</strong>.</li>
              </ol>

              <h3>Enabling / Disabling Sources</h3>
              <p>
                Use the <strong>toggle switch</strong> next to each source to enable or disable it.
                Disabled sources are skipped during discovery.
              </p>

              <h3>Source Trust Tiers</h3>
              <div className="help-table-wrap">
                <table className="help-table">
                  <thead><tr><th>Tier</th><th>Examples</th><th>Eligibility</th></tr></thead>
                  <tbody>
                    <tr><td><span className="help-tag green">A</span></td><td>LME, SMM, Reuters, Bloomberg, company official sites</td><td>Automatic</td></tr>
                    <tr><td><span className="help-tag blue">B</span></td><td>Light Metal Age, Business Standard, trade publications</td><td>Automatic</td></tr>
                    <tr><td><span className="help-tag grey">C</span></td><td>Smaller blogs, unknown outlets</td><td>Flagged for review</td></tr>
                    <tr><td><span className="help-tag red">REJECT</span></td><td>Press-release wires, market-research spam</td><td>Blocked</td></tr>
                  </tbody>
                </table>
              </div>

              <h3>Source Statistics</h3>
              <p>
                The panel header shows total active sources and how many articles have been
                generated from each source. Each source entry displays a green badge with the
                article count.
              </p>
            </section>

            {/* ── REVISION ─────────────────────────────────── */}
            <section id="help-revision" className="help-section">
              <h2><span>✎</span> Revisions & Feedback</h2>

              <h3>Requesting an AI Revision</h3>
              <ol className="help-ordered">
                <li>Open the article in <strong>Edit</strong> mode.</li>
                <li>Scroll to the <strong>Editorial Feedback</strong> section.</li>
                <li>Type your feedback in the text area — be specific about what to change.</li>
                <li>Click <strong>✎ Revise Article</strong>.</li>
                <li>The AI rewrites the article incorporating your feedback while preserving all facts.</li>
              </ol>

              <div className="help-notice-small">
                Good feedback examples:
                <ul className="help-list" style={{marginTop:'0.5rem'}}>
                  <li>"Make the lede more concise — lead with the price figure."</li>
                  <li>"Add context about why the LME inventory figure is significant."</li>
                  <li>"The headline is too long. Shorten to under 75 characters."</li>
                </ul>
              </div>

              <h3>Viewing Revision History</h3>
              <p>
                Click <strong>📜 History</strong> on any article that has been revised. The history
                modal shows each revision with its timestamp, the feedback you provided, and a
                side-by-side view of what changed.
              </p>

              <h3>Revision Counter</h3>
              <p>
                The article modal shows <em>✓ Revised N×</em> when an article has been through
                revisions. The maximum is 3 AI rewrite rounds; after that, the article is routed to
                human review.
              </p>
            </section>

            {/* ── PUBLISH ──────────────────────────────────── */}
            <section id="help-publish" className="help-section">
              <h2><span>📢</span> Publishing & Export</h2>

              <h3>Publishing an Article</h3>
              <ol className="help-ordered">
                <li>Open the article and review it thoroughly.</li>
                <li>Make any edits and click <strong>Save Changes</strong> if needed.</li>
                <li>Click the green <strong>📢 Publish</strong> button in the modal header.</li>
                <li>The article status changes to <em>published</em> and appears on the Published tab.</li>
              </ol>

              <div className="help-notice-small">
                The <strong>Publish</strong> button is disabled when:
                <ul className="help-list" style={{marginTop:'0.5rem'}}>
                  <li>There are unsaved changes (save first)</li>
                  <li>The article is already published</li>
                  <li>The pipeline is still running</li>
                </ul>
              </div>

              <h3>Downloading as PDF</h3>
              <p>
                Click <strong>⬇ PDF</strong> on any article card or inside the article modal to
                download a formatted PDF version. The PDF includes the headline, image, body
                content, and source attribution.
              </p>

              <h3>Regenerating Images</h3>
              <p>
                If an article image is missing or unsatisfactory, click
                <strong> 🖼 Regenerate Missing Images</strong> (available from the dashboard
                header area). This re-triggers AI image generation for all articles that are
                missing a generated image.
              </p>
            </section>

            {/* ── TIPS ─────────────────────────────────────── */}
            <section id="help-tips" className="help-section">
              <h2><span>💡</span> Tips & Best Practices</h2>

              <div className="help-tips-grid">
                <div className="help-tip">
                  <div className="help-tip-icon">🎯</div>
                  <h4>Check sources before generating</h4>
                  <p>Open <strong>📡 Sources</strong> and ensure your high-quality feeds are enabled. Tier A sources produce the best Fact Banks and highest-quality articles.</p>
                </div>
                <div className="help-tip">
                  <div className="help-tip-icon">🔄</div>
                  <h4>Pipeline runs in background</h4>
                  <p>After clicking Generate, you can safely switch tabs or step away. Come back in 5–10 minutes — the articles will be waiting for you.</p>
                </div>
                <div className="help-tip">
                  <div className="help-tip-icon">📊</div>
                  <h4>Review quality scores first</h4>
                  <p>Before reading the full article, check the scores. An originality below 70% means the article needs a revision. A style score below 60 means editorial polish is needed.</p>
                </div>
                <div className="help-tip">
                  <div className="help-tip-icon">✎</div>
                  <h4>Be specific with revision feedback</h4>
                  <p>Vague feedback like "make it better" rarely improves an article. Specify what to change: headline length, paragraph structure, missing context, tone, etc.</p>
                </div>
                <div className="help-tip">
                  <div className="help-tip-icon">📡</div>
                  <h4>Use the log viewer for details</h4>
                  <p>Click <strong>📊 View Logs</strong> to see exactly what happened during generation — which sources were used, fact counts, quality gate results, and any issues.</p>
                </div>
                <div className="help-tip">
                  <div className="help-tip-icon">⚠️</div>
                  <h4>needs_review articles need attention</h4>
                  <p>Articles with <em>needs_review</em> status were flagged by a quality gate. Read the review reasons and either request a revision or edit manually before publishing.</p>
                </div>
              </div>
            </section>

            {/* ── FAQ ──────────────────────────────────────── */}
            <section id="help-faq" className="help-section">
              <h2><span>❓</span> FAQ</h2>

              <div className="help-faq-list">
                <details className="help-faq-item">
                  <summary>Why does the pipeline show "Processing…" for a long time?</summary>
                  <p>
                    The pipeline involves multiple LLM calls for fact extraction, article generation,
                    style analysis, and claim verification. A single article typically takes 2–5 minutes.
                    A batch of 3–5 articles can take 10–15 minutes. This is normal. You do not need
                    to wait on the page — the pipeline runs server-side.
                  </p>
                </details>

                <details className="help-faq-item">
                  <summary>What does "needs_review" mean exactly?</summary>
                  <p>
                    It means one or more quality gates fired. Common reasons include: originality
                    below the 70% threshold, a claim that couldn't be verified against the Fact Bank,
                    a fidelity error (article says something the source doesn't support), or a style
                    score below the minimum. Check the article's review reasons for the specific cause.
                  </p>
                </details>

                <details className="help-faq-item">
                  <summary>Can I add my own news sources?</summary>
                  <p>
                    Yes. Open <strong>📡 Sources</strong>, click Add Source, and enter the RSS feed
                    URL. Custom sources are labelled with a "CUSTOM" badge. If the source is a Tier C
                    or unknown outlet, the system will flag articles from it for extra review.
                  </p>
                </details>

                <details className="help-faq-item">
                  <summary>Why do some articles have no image?</summary>
                  <p>
                    AI image generation can fail if the prompt is too complex or the image service
                    is unavailable. In that case, a stock aluminium industry image is used as a
                    fallback. Use <strong>Regenerate Missing Images</strong> from the dashboard to retry.
                  </p>
                </details>

                <details className="help-faq-item">
                  <summary>Will articles be automatically published?</summary>
                  <p>
                    No. Publication requires a human editor to explicitly click the
                    <strong> Publish</strong> button. The system is designed as an editorial
                    assistant — all final publishing decisions remain with your editorial team.
                  </p>
                </details>

                <details className="help-faq-item">
                  <summary>What happens if I close the tab while the pipeline is running?</summary>
                  <p>
                    Nothing bad. The pipeline runs on the server, completely independent of your
                    browser. Close the tab, shut the laptop, come back later — your articles will
                    be ready in the dashboard when you return.
                  </p>
                </details>

                <details className="help-faq-item">
                  <summary>How do I know the facts in the article are accurate?</summary>
                  <p>
                    Every fact in a generated article must be grounded in verbatim source evidence.
                    The pipeline runs a Fidelity Check (verifies all values, dates, and quotes match
                    the source) and a Claim Verification pass (checks every factual statement against
                    the Fact Bank). Articles with fact errors are routed to <em>needs_review</em>
                    rather than passed automatically.
                  </p>
                </details>

                <details className="help-faq-item">
                  <summary>Can I revise an article more than once?</summary>
                  <p>
                    Yes, up to 3 AI revision rounds. After that, further revisions must be done
                    manually in the editor. The revision history is always preserved so you can
                    see every version.
                  </p>
                </details>

                <details className="help-faq-item">
                  <summary>How do I report a problem or get support?</summary>
                  <p>
                    Use the <strong>📊 View Logs</strong> button to access the generation log viewer,
                    which shows detailed pipeline output. Contact your system administrator with the
                    workflow ID shown in the log for investigation.
                  </p>
                </details>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
